import { useCallback, useEffect, useMemo, useState } from "react";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";
import { useAuth } from "@/context/AuthContext";
import type { Task } from "@/types/task";
import type { WaConversation } from "@/types/whatsapp";
import type { ProposalListRow } from "@/types/proposal";
import type { SalesExecSummary } from "../SalesExecDashboard";
import { dateLocale, withClock } from "@/lib/dateTime";
import type { Visit } from "@/lib/visits";

export type QueueFolder = "all" | "followups" | "replies" | "approvals" | "overdue" | "snoozed";
export type QueueKind = "overdue" | "task" | "view" | "reply" | "approve" | "review" | "waiting" | "lead" | "visit" | "risk";
export type QueueGroup = "overdue" | "now" | "later";

export interface QuoteToApprove {
  id: string;
  display_number: string;
  grand_total: number | string;
  account?: { name: string } | null;
  deal?: { id: string; name: string } | null;
  owner?: { first_name: string; last_name?: string | null } | null;
  max_discount_pct?: number | string;
  created_on: string;
}

export interface QueueItem {
  key: string;
  kind: QueueKind;
  folder: Exclude<QueueFolder, "all" | "snoozed">;
  group: QueueGroup;
  title: string;
  /** Who it is about: account, company or contact */
  account: string;
  preview: string;
  /** Sort key: most urgent first within a group */
  at: number;
  timeLabel: string;
  overdue?: boolean;
  task?: Task;
  proposal?: ProposalListRow;
  quote?: QuoteToApprove;
  /** A quote or proposal this person sent for approval and is waiting on */
  waiting?: { kind: "QUOTE" | "PROPOSAL"; id: string; display_number: string; grand_total: number; submitted_at: string; account_name: string; approver_name: string | null };
  chat?: WaConversation;
  visit?: Visit;
  lead?: SalesExecSummary["hot_leads"][number] | SalesExecSummary["new_leads"][number];
  /** An own deal the AI's nightly check marked at risk */
  risk?: DealRisk;
}

export interface DealRisk {
  deal_id: string;
  name: string;
  amount: number;
  summary: string | null;
  next_action: string | null;
  assessed_at: string;
}

const DAY = 86_400_000;

function ago(iso?: string | null) {
  if (!iso) return "";
  const min = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (min < 1) return "now";
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function clock(iso: string) {
  return new Date(iso).toLocaleTimeString(dateLocale(), withClock({ hour: "2-digit", minute: "2-digit" }));
}

const TASK_VERB: Record<string, string> = {
  CALL: "Call",
  EMAIL: "Email",
  MEETING: "Meeting",
  FOLLOW_UP: "Follow-up",
  WHATSAPP: "WhatsApp",
  OTHER: "Task",
};

async function pageOf<T>(path: string): Promise<T[]> {
  const res = await api.get(path);
  return unwrapPage<T>(res).rows;
}

/** Everything a rep has to act on today, gathered from the existing endpoints */
export function useMyDay() {
  const { hasPermission, user } = useAuth();
  const [items, setItems] = useState<QueueItem[]>([]);
  const [summary, setSummary] = useState<SalesExecSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  // Items completed or dismissed in this session, so they vanish at once
  const [done, setDone] = useState<Set<string>>(new Set());
  // Items put aside until a time (kept on the server), and the numbers the dashboard summary lacks
  const [snoozes, setSnoozes] = useState<Record<string, string>>({});
  const [doneKeys, setDoneKeys] = useState<Set<string>>(new Set());
  const [numbers, setNumbers] = useState<{ calls_this_week: number; days_left: number } | null>(null);

  const can = {
    tasks: hasPermission("activity.read"),
    whatsapp: hasPermission("whatsapp.read"),
    proposals: hasPermission("proposal.read"),
    reviewProposals: hasPermission("proposal.approve"),
    approveQuotes: hasPermission("quote.approve"),
    visits: hasPermission("visit.create"),
    risks: hasPermission("ai.use") && hasPermission("deal.read"),
  };

  const load = useCallback(async () => {
    setLoading(true);
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const range = `from=${encodeURIComponent(start.toISOString())}&to=${encodeURIComponent(end.toISOString())}`;

    api
      .get("/api/MyDay/Snoozes")
      .then((r) => {
        const rows = unwrapArray<{ item_key: string; until: string; done?: boolean }>(r);
        setSnoozes(Object.fromEntries(rows.map((x) => [x.item_key, x.until])));
        setDoneKeys(new Set(rows.filter((x) => x.done).map((x) => x.item_key)));
      })
      .catch(() => undefined);
    api
      .get("/api/MyDay/Numbers")
      .then((r) => setNumbers(unwrapObject(r)))
      .catch(() => undefined);

    const [overdue, today, chats, viewed, review, approve, dash, visits, risks] = await Promise.allSettled([
      can.tasks ? pageOf<Task>(`/api/Task/List?view=overdue&scope=mine&limit=30&${range}`) : Promise.resolve([] as Task[]),
      can.tasks ? pageOf<Task>(`/api/Task/List?view=today&scope=mine&limit=30&${range}`) : Promise.resolve([] as Task[]),
      can.whatsapp ? pageOf<WaConversation>("/api/WhatsApp/Conversations?view=unread&page_size=15") : Promise.resolve([] as WaConversation[]),
      can.proposals ? pageOf<ProposalListRow>("/api/Proposal/List?status=VIEWED&page_size=15") : Promise.resolve([] as ProposalListRow[]),
      can.reviewProposals ? pageOf<ProposalListRow>("/api/Proposal/List?mine_to_review=true&page_size=10") : Promise.resolve([] as ProposalListRow[]),
      can.approveQuotes ? pageOf<QuoteToApprove>("/api/Quotation/List?mine_to_approve=true&page_size=10") : Promise.resolve([] as QuoteToApprove[]),
      api.get("/api/Dashboard/Summary").then((r) => unwrapObject<SalesExecSummary & { role: string }>(r)),
      can.visits && user?.Id ? pageOf<Visit>(`/api/FieldVisit/List?view=today&owner_id=${user.Id}&page_size=30`) : Promise.resolve([] as Visit[]),
      // Empty while the AI or its deal health check is off
      can.risks ? api.get("/api/Ai/DealHealth/Mine").then((r) => unwrapArray<DealRisk>(r)) : Promise.resolve([] as DealRisk[]),
    ]);

    const val = <T,>(r: PromiseSettledResult<T[]>) => (r.status === "fulfilled" ? r.value : []);
    const list: QueueItem[] = [];
    const now = Date.now();

    for (const t of val(overdue)) {
      const days = Math.max(1, Math.floor((now - new Date(t.due_at).getTime()) / DAY));
      list.push({
        key: `task:${t.id}`,
        kind: "overdue",
        folder: "overdue",
        group: "overdue",
        title: t.title,
        account: t.related_name || "",
        preview: `${TASK_VERB[t.task_type] || "Task"} · was due ${new Date(t.due_at).toLocaleDateString(dateLocale(), { day: "2-digit", month: "short" })}`,
        at: new Date(t.due_at).getTime(),
        timeLabel: `${days}d`,
        overdue: true,
        task: t,
      });
    }

    for (const p of val(review)) {
      list.push({
        key: `review:${p.id}`,
        kind: "review",
        folder: "approvals",
        group: "now",
        title: `Review proposal ${p.display_number}`,
        account: p.account?.name || p.deal?.name || "",
        preview: p.title,
        at: new Date(p.created_on).getTime(),
        timeLabel: ago(p.created_on),
        proposal: p,
      });
    }

    for (const q of val(approve)) {
      const who = q.owner ? `${q.owner.first_name} ${q.owner.last_name || ""}`.trim() : "";
      list.push({
        key: `approve:${q.id}`,
        kind: "approve",
        folder: "approvals",
        group: "now",
        title: `Approve quote ${q.display_number}`,
        account: q.account?.name || q.deal?.name || "",
        preview: `${who ? `Requested by ${who}` : "Waiting for your approval"}${Number(q.max_discount_pct) > 0 ? ` · up to ${Number(q.max_discount_pct)}% off` : ""}`,
        at: new Date(q.created_on).getTime(),
        timeLabel: ago(q.created_on),
        quote: q,
      });
    }

    // What this person has sent up and is waiting on (reps; heads see theirs above as things to decide)
    for (const w of (dash.status === "fulfilled" ? (dash.value as any).waiting_approval : undefined) || []) {
      list.push({
        key: `waiting:${w.kind}:${w.id}`,
        kind: "waiting",
        folder: "approvals",
        group: "later",
        title: `${w.kind === "PROPOSAL" ? "Proposal" : "Quote"} ${w.display_number} is waiting for approval`,
        account: w.account_name || "",
        preview: `Waiting for ${w.approver_name || "a reviewer"} · ${ago(w.submitted_at)}`,
        at: new Date(w.submitted_at).getTime(),
        timeLabel: ago(w.submitted_at),
        waiting: w,
      });
    }

    for (const c of val(chats)) {
      list.push({
        key: `chat:${c.id}`,
        kind: "reply",
        folder: "replies",
        group: "now",
        title: c.unread_count > 1 ? `${c.unread_count} new WhatsApp messages` : "New WhatsApp message",
        account: c.profile_name || c.phone,
        preview: c.last_message_preview || "",
        at: c.last_message_at ? new Date(c.last_message_at).getTime() : now,
        timeLabel: ago(c.last_message_at),
        chat: c,
      });
    }

    for (const p of val(viewed)) {
      list.push({
        key: `view:${p.id}`,
        kind: "view",
        folder: "followups",
        group: "now",
        title: p.view_count > 1 ? `Proposal opened ${p.view_count} times` : "Proposal opened",
        account: p.account?.name || p.deal?.name || "",
        preview: `${p.display_number} · ${p.title}`,
        at: new Date(p.first_viewed_at || p.sent_at || p.created_on).getTime(),
        timeLabel: ago(p.first_viewed_at || p.sent_at),
        proposal: p,
      });
    }

    for (const r of val(risks)) {
      list.push({
        key: `risk:${r.deal_id}`,
        kind: "risk",
        folder: "followups",
        group: "now",
        title: `At risk: ${r.name}`,
        account: r.summary || "",
        preview: r.next_action ? `Next: ${r.next_action}` : "The AI sees this deal at risk",
        at: new Date(r.assessed_at).getTime(),
        timeLabel: ago(r.assessed_at),
        risk: r,
      });
    }

    // Today's planned visits, and the one the rep is on now
    for (const v of val(visits)) {
      if (v.visit_status !== "PLANNED" && v.visit_status !== "CHECKED_IN") continue;
      const at = v.check_in_at || v.planned_at;
      list.push({
        key: `visit:${v.id}`,
        kind: "visit",
        folder: "followups",
        group: v.visit_status === "CHECKED_IN" ? "now" : "later",
        title: v.visit_status === "CHECKED_IN" ? `On visit: ${v.related_label}` : `Visit ${v.related_label}`,
        account: v.related_label,
        preview: v.purpose || (v.visit_status === "CHECKED_IN" ? "Check out when you leave" : "Planned visit"),
        at: at ? new Date(at).getTime() : now,
        timeLabel: at ? clock(at) : "",
        visit: v,
      });
    }

    for (const t of val(today)) {
      list.push({
        key: `task:${t.id}`,
        kind: "task",
        folder: "followups",
        group: "later",
        title: t.title,
        account: t.related_name || "",
        preview: TASK_VERB[t.task_type] || "Task",
        at: new Date(t.due_at).getTime(),
        timeLabel: t.all_day ? "All day" : clock(t.due_at),
        task: t,
      });
    }

    const d = dash.status === "fulfilled" ? (dash.value as SalesExecSummary & { role: string }) : null;
    if (d && d.role === "SALES_EXEC") {
      setSummary(d);
      const seen = new Set<string>();
      for (const l of d.hot_leads || []) {
        seen.add(l.id);
        list.push({
          key: `lead:${l.id}`,
          kind: "lead",
          folder: "followups",
          group: "now",
          title: `Hot lead: ${l.name || l.company_name}`,
          account: l.company_name || "",
          preview: l.next_follow_up_at ? `Follow-up due ${new Date(l.next_follow_up_at).toLocaleDateString(dateLocale(), { day: "2-digit", month: "short" })}` : `Score ${l.score}`,
          at: l.next_follow_up_at ? new Date(l.next_follow_up_at).getTime() : now,
          timeLabel: l.next_follow_up_at ? ago(l.next_follow_up_at) : "",
          lead: l,
        });
      }
      for (const l of d.new_leads || []) {
        if (seen.has(l.id)) continue;
        list.push({
          key: `lead:${l.id}`,
          kind: "lead",
          folder: "followups",
          group: "later",
          title: `New lead: ${l.name || l.company_name}`,
          account: l.company_name || "",
          preview: l.source ? `From ${l.source}` : "Assigned to you",
          at: new Date(l.assigned_at || l.created_on).getTime(),
          timeLabel: ago(l.assigned_at || l.created_on),
          lead: l,
        });
      }
    }

    setFailed(
      [overdue, today, chats, viewed, review, approve].some((r) => r.status === "rejected") && list.length === 0,
    );
    setItems(list);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [can.tasks, can.whatsapp, can.proposals, can.reviewProposals, can.approveQuotes, can.risks]);

  useEffect(() => {
    load();
  }, [load]);

  const live = useMemo(() => items.filter((i) => !done.has(i.key)), [items, done]);
  // A snooze ends by itself when its time passes, even while the page stays open
  const isSnoozed = (key: string) => !!snoozes[key] && new Date(snoozes[key]).getTime() > Date.now();
  const visible = useMemo(() => live.filter((i) => !isSnoozed(i.key)), [live, snoozes]);
  const snoozed = useMemo(() => live.filter((i) => isSnoozed(i.key) && !doneKeys.has(i.key)), [live, snoozes, doneKeys]);

  const counts = useMemo(
    () => ({
      all: visible.length,
      followups: visible.filter((i) => i.folder === "followups").length,
      replies: visible.filter((i) => i.folder === "replies").length,
      approvals: visible.filter((i) => i.folder === "approvals").length,
      overdue: visible.filter((i) => i.folder === "overdue").length,
      snoozed: snoozed.length,
    }),
    [visible, snoozed],
  );

  const dismiss = (key: string) => setDone((prev) => new Set(prev).add(key));

  const snooze = async (key: string, until: Date) => {
    await api.post("/api/MyDay/Snooze", { item_key: key, until: until.toISOString() });
    setSnoozes((cur) => ({ ...cur, [key]: until.toISOString() }));
  };
  const markDone = async (key: string) => {
    await api.post("/api/MyDay/Done", { item_key: key });
    setSnoozes((cur) => ({ ...cur, [key]: new Date(Date.now() + 30 * 86400000).toISOString() }));
    setDoneKeys((cur) => new Set(cur).add(key));
  };
  const unsnooze = async (key: string) => {
    await api.post("/api/MyDay/Unsnooze", { item_key: key });
    setSnoozes((cur) => {
      const next = { ...cur };
      delete next[key];
      return next;
    });
    setDoneKeys((cur) => {
      const next = new Set(cur);
      next.delete(key);
      return next;
    });
  };

  return { items: visible, snoozed, snoozes, counts, summary, numbers, loading, failed, reload: load, dismiss, snooze, unsnooze, markDone };
}
